/* eslint-disable @typescript-eslint/no-explicit-any */
import * as action_types from "./constants";
import {
  GetDealType,
  MaterialsType,
  ProductionType,
  ProposalCompanyDataType,
  ProposalDetailType,
  ServiceGroupType,
  ServiceLineType,
  ServiceType,
  SubmitEstimateType,
} from "./types";

export type myCompanyState = {
  dealsListData: {
    loader: boolean;
    data: [] | null;
    count: number;
  };
  dealsDetailData: {
    loader: boolean;
    data: GetDealType | null;
  };
  dealFormData: {
    managers: [] | null;
    property: [] | null;
  };
  estimateFormData: {
    services: ServiceType[] | null;
    materials: MaterialsType[] | null;
    service_lines: ServiceLineType[] | null;
    service_groups: ServiceGroupType[] | null;
  };
  proposalData: {
    data: SubmitEstimateType | null;
    loader: boolean;
  };
  proposalDetailData: null | ProposalDetailType;
  currentProposalData: {
    data: SubmitEstimateType | null;
    loader: boolean;
  };
  currentProposalDetailData: null | ProposalDetailType;
  proposalBuilderData: {
    data: ProposalCompanyDataType | null;
    loader: boolean;
  };
  proposalDocument: {
    data: {
      html: string | null;
      signature_url: string | null;
      table_columns: string[];
      id: string | null;
      client_contact?: {
        email: string;
        first_name: string;
        id: string;
        last_name: string;
        phone: string;
        title: string;
      };
    };
    loader: boolean;
  };
  proposalListData: {
    loader: boolean;
    data: [] | null;
    count: number;
  };
  productionFormData: {
    loader: boolean;
    data: ProductionType | null;
  };
};

const initialState: myCompanyState = {
  dealsListData: {
    loader: true,
    data: null,
    count: 0,
  },
  dealsDetailData: {
    loader: true,
    data: null,
  },
  dealFormData: {
    managers: null,
    property: null,
  },
  estimateFormData: {
    services: null,
    service_lines: null,
    service_groups: null,
    materials: null,
  },
  proposalData: {
    data: null,
    loader: true,
  },
  proposalDetailData: null,
  currentProposalData: {
    data: null,
    loader: true,
  },
  currentProposalDetailData: null,
  proposalBuilderData: {
    data: null,
    loader: true,
  },
  proposalDocument: {
    data: {
      html: null,
      signature_url: null,
      table_columns: [],
      id: null,
    },
    loader: true,
  },
  proposalListData: {
    loader: true,
    data: null,
    count: 0,
  },
  productionFormData: {
    loader: true,
    data: null,
  },
};

type MyCompanyActionType =
  | {
      type: typeof action_types.GET_DEALS_LISTING;
      data: { payload: []; loading: boolean; count: number };
    }
  | {
      type: typeof action_types.GET_DEALS_DETAIL;
      data: { payload: GetDealType; loading: boolean };
    }
  | {
      type: typeof action_types.GET_DEALS_FORM_DATA;
      data: {
        managers: [] | null;
        property: [] | null;
      };
    }
  | {
      type: typeof action_types.GET_ESTIMATE_FORM_DATA;
      data: {
        services: ServiceType[] | null;
        materials: MaterialsType[] | null;
        service_groups: ServiceGroupType[] | null;
        service_lines: ServiceLineType[] | null;
      };
    }
  | {
      type: typeof action_types.GET_PROPOSAL_BY_ID;
      data: { payload: SubmitEstimateType; loading: boolean };
    }
  | {
      type: typeof action_types.GET_PROPOSAL_DETAIL_BY_ID;
      data: ProposalDetailType | null;
    }
  | {
      type: typeof action_types.GET_PROPOSAL_BUILDER_DATA;
      data: { payload: any; loading: boolean };
    }
  | {
      type: typeof action_types.GET_PROPOSAL_BUILDER;
      data: { payload: any; loading: boolean };
    }
  | {
      type: typeof action_types.GET_PROPOSAL_LISTING_DATA;
      data: { payload: []; loading: boolean; count: number };
    }
  | {
      type: typeof action_types.SEND_TO_PRODUCTION_FORM_DATA;
      data: { payload: ProductionType; loading: boolean };
    }
  | {
      type: typeof action_types.GET_CURRENT_PROPOSAL_BY_ID;
      data: { payload: SubmitEstimateType; loading: boolean };
    }
  | {
      type: typeof action_types.GET_CURRENT_PROPOSAL_DETAIL_BY_ID;
      data: ProposalDetailType | null;
    };

const myCompanyReducer = (
  state = initialState,
  action: MyCompanyActionType
): myCompanyState => {
  const { type, data } = action;
  switch (type) {
    case action_types.GET_DEALS_LISTING:
      return {
        ...state,
        dealsListData: {
          loader: data?.loading,
          data: data?.payload,
          count: data?.count,
        },
      };

    case action_types.GET_DEALS_DETAIL:
      return {
        ...state,
        dealsDetailData: { data: data?.payload, loader: data?.loading },
      };

    case action_types.GET_DEALS_FORM_DATA:
      return {
        ...state,
        dealFormData: data,
      };

    case action_types.GET_ESTIMATE_FORM_DATA:
      return {
        ...state,
        estimateFormData: data,
      };

    case action_types.GET_PROPOSAL_BY_ID:
      return {
        ...state,
        proposalData: { data: data?.payload, loader: data?.loading },
      };

    case action_types.GET_PROPOSAL_DETAIL_BY_ID:
      return {
        ...state,
        proposalDetailData: data,
      };

    case action_types.GET_CURRENT_PROPOSAL_BY_ID:
      return {
        ...state,
        currentProposalData: { data: data?.payload, loader: data?.loading },
      };

    case action_types.GET_CURRENT_PROPOSAL_DETAIL_BY_ID:
      return {
        ...state,
        currentProposalDetailData: data,
      };

    case action_types.GET_PROPOSAL_BUILDER_DATA:
      return {
        ...state,
        proposalBuilderData: { data: data?.payload, loader: data?.loading },
      };

    case action_types.GET_PROPOSAL_BUILDER:
      return {
        ...state,
        proposalDocument: { data: data?.payload, loader: data?.loading },
      };

    case action_types.GET_PROPOSAL_LISTING_DATA:
      return {
        ...state,
        proposalListData: {
          loader: data?.loading,
          data: data?.payload,
          count: data?.count,
        },
      };

    case action_types.SEND_TO_PRODUCTION_FORM_DATA:
      return {
        ...state,
        productionFormData: { data: data?.payload, loader: data?.loading },
      };

    default:
      return state;
  }
};

export default myCompanyReducer;
